// src/components/CreatorInfoPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db, storage } from '../firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs, limit, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './CreatorInfoPage.css';

// Country list (matches mobile - 20 countries)
const COUNTRY_LIST = [
  'Argentina','Australia','Brazil','Canada','China','Egypt','France','Germany',
  'India','Indonesia','Italy','Japan','Mexico','Nigeria','Philippines',
  'South Africa','South Korea','Spain','United Kingdom','United States',
];

// Language list (matches mobile)
const LANGUAGE_LIST = ['English (American)', 'English (British)'];

// Currency list (matches mobile)
const CURRENCY_LIST = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'KRW', symbol: '₩', name: 'Korean Won' },
  { code: 'ALL', symbol: 'L', name: 'Albanian Lek' },
];

export default function CreatorInfoPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [nationality, setNationality] = useState('');
  const [language, setLanguage] = useState('');
  const [gender, setGender] = useState('');
  const [dob, setDob] = useState('');
  const [preferredCurrency, setPreferredCurrency] = useState('USD');
  const [venmoUsername, setVenmoUsername] = useState('');
  const [preferAlternativeReimbursement, setPreferAlternativeReimbursement] = useState(false);
  const [instagram, setInstagram] = useState('');
  const [tiktok, setTiktok] = useState('');
  const [otherSocial, setOtherSocial] = useState('');
  const [creatorStory, setCreatorStory] = useState('');
  const [travelExperience, setTravelExperience] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDetails, setPaymentDetails] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Validation errors
  const [errors, setErrors] = useState({});

  // Authentication state
  const [needsSignup, setNeedsSignup] = useState(false);
  const [signupData, setSignupData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [signupError, setSignupError] = useState('');

  // Payment verification
  const [accessDenied, setAccessDenied] = useState(false);

  // Helper function to check if user is admin
  const isAdmin = async (currentUser) => {
    if (!currentUser) return false;

    // Check hardcoded admin emails
    const adminEmails = [
      'matthew@backpass.co',
      'mgriff7@gmail.com',
      'mhgbackpass@gmail.com',
      'mhgcommercial@gmail.com'
    ];

    if (adminEmails.includes(currentUser.email)) {
      return true;
    }

    // Check user document for isAdmin flag
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists() && userDoc.data().isAdmin === true) {
        return true;
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }

    return false;
  };

  // Check if user is authenticated and load existing creator profile
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      // PAYMENT VERIFICATION: Check if email parameter exists and has valid transaction
      const urlParams = new URLSearchParams(location.search);
      const emailParam = urlParams.get('email');

      console.log('🔍 CreatorInfoPage - Auth State:', {
        authenticated: !!currentUser,
        currentUserEmail: currentUser?.email,
        emailParam: emailParam,
        url: window.location.href
      });

      if (!currentUser) {
        // Not authenticated - need to verify payment before showing signup form
        console.log('⚠️ User not authenticated, checking email parameter...');

        // If no email parameter provided, deny access
        if (!emailParam) {
          console.error('❌ No email parameter found in URL');
          setAccessDenied(true);
          setLoading(false);
          return;
        }

        console.log('✅ Email parameter found:', emailParam);

        // Verify email exists in creatorTransactions (payment proof)
        try {
          const transactionsQuery = query(
            collection(db, 'creatorTransactions'),
            where('customerEmail', '==', emailParam),
            limit(1)
          );
          const transactionsSnapshot = await getDocs(transactionsQuery);

          if (transactionsSnapshot.empty) {
            // No payment transaction found - check if this is a manually created account with paymentVerified flag
            console.log('📝 No transaction found, checking for manually created account...');
            console.log('🔎 Querying users collection for email:', emailParam.toLowerCase());

            try {
              const usersQuery = query(
                collection(db, 'users'),
                where('email', '==', emailParam.toLowerCase()),
                limit(1)
              );
              const usersSnapshot = await getDocs(usersQuery);

              console.log('📊 Users query result:', {
                empty: usersSnapshot.empty,
                size: usersSnapshot.size,
                docs: usersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
              });

              if (!usersSnapshot.empty) {
                const userData = usersSnapshot.docs[0].data();

                console.log('👤 User data found:', {
                  email: userData.email,
                  paymentVerified: userData.paymentVerified,
                  isCreator: userData.isCreator
                });

                if (userData.paymentVerified === true) {
                  // Manually created account found - allow access
                  console.log('✅ Manually created creator account detected - granting access');
                  setNeedsSignup(true);
                  setSignupData(prev => ({ ...prev, email: emailParam }));
                  setLoading(false);
                  return;
                } else {
                  console.log('⚠️ User found but paymentVerified is not true:', userData.paymentVerified);
                }
              } else {
                console.log('⚠️ No user document found with email:', emailParam.toLowerCase());
              }
            } catch (userCheckError) {
              console.error('❌ Error checking for manually created account:', userCheckError);
              console.error('Error details:', {
                code: userCheckError.code,
                message: userCheckError.message,
                stack: userCheckError.stack
              });
            }

            // No payment found and no manually created account - deny access
            console.error('❌ No payment transaction or manually created account found for:', emailParam);
            setAccessDenied(true);
            setLoading(false);
            return;
          }

          // Payment verified! Load data from transaction
          setNeedsSignup(true);

          // Pre-fill signup form with verified email
          setSignupData(prev => ({ ...prev, email: emailParam }));

        } catch (error) {
          console.error('Error verifying payment:', error);
          setAccessDenied(true);
          setLoading(false);
          return;
        }

        setLoading(false);
        return;
      }

      setNeedsSignup(false);

      // Check if user is admin - admins bypass payment verification
      const userIsAdmin = await isAdmin(currentUser);

      // Authenticated user - check if they have existing user document with creator flag
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists() && userDoc.data().isCreator) {
          // Existing creator - allow them to edit their profile
          // Load existing creator profile from user document
          const data = userDoc.data();
          const creatorProfile = data.creatorProfile || {};

          // Load first and last name
          setFirstName(data.firstName || '');
          setLastName(data.lastName || '');

          setEmail(data.email || currentUser.email || '');
          setPhoneNumber(data.phoneNumber || '');
          setNationality(data.nationality || '');
          setLanguage(data.language || '');
          setGender(data.gender || '');
          setDob(data.dob || '');
          setPreferredCurrency(data.preferredCurrency || 'USD');
          setVenmoUsername(data.venmoUsername || '');
          setPreferAlternativeReimbursement(data.preferAlternativeReimbursement || false);
          setInstagram(creatorProfile.socialMedia?.instagram || '');
          setTiktok(creatorProfile.socialMedia?.tiktok || '');
          setOtherSocial(creatorProfile.socialMedia?.other || '');
          setCreatorStory(creatorProfile.creatorStory || '');
          setTravelExperience(creatorProfile.travelExperience || '');
          setPaymentMethod(creatorProfile.paymentMethod || '');
          setPaymentDetails(creatorProfile.paymentDetails || '');
          setProfilePhotoPreview(creatorProfile.profilePhotoUrl || null);
        } else {
          // No existing profile - verify payment OR allow if admin OR allow if manually created with paymentVerified flag
          if (userIsAdmin) {
            // Admin access - just pre-fill email
            console.log('Admin access granted to creator info page');
            setEmail(currentUser.email || '');
          } else {
            // Check if this is a manually created account with paymentVerified flag
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.exists() ? userDoc.data() : null;

            if (userData && userData.paymentVerified === true) {
              // Manually created account - allow access
              console.log('Manually created creator account detected - granting access');

              // Pre-fill any existing data
              setFirstName(userData.firstName || '');
              setLastName(userData.lastName || '');
              setEmail(userData.email || currentUser.email || '');
            } else {
              // Non-admin without paymentVerified flag - MUST verify payment via transaction
              try {
                // Verify email exists in creatorTransactions (payment proof)
                if (!currentUser.email) {
                  console.error('No email found for authenticated user');
                  setAccessDenied(true);
                  setLoading(false);
                  return;
                }

                const transactionsQuery = query(
                  collection(db, 'creatorTransactions'),
                  where('customerEmail', '==', currentUser.email),
                  limit(1)
                );
                const transactionsSnapshot = await getDocs(transactionsQuery);

                if (transactionsSnapshot.empty) {
                  // No payment found - deny access
                  console.error('No payment transaction found for authenticated user:', currentUser.email);
                  setAccessDenied(true);
                  setLoading(false);
                  return;
                }
              } catch (error) {
                console.error('Error verifying payment for authenticated user:', error);
                setAccessDenied(true);
                setLoading(false);
                return;
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading creator profile:', error);
        setEmail(currentUser.email || '');
      }

      setLoading(false);
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.state]);

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupError('');

    // Validation
    if (!signupData.email || !signupData.password || !signupData.confirmPassword) {
      setSignupError('Please fill in all fields');
      return;
    }

    if (signupData.password.length < 6) {
      setSignupError('Password must be at least 6 characters');
      return;
    }

    if (signupData.password !== signupData.confirmPassword) {
      setSignupError('Passwords do not match');
      return;
    }

    setSaving(true);

    try {
      // Create user account
      await createUserWithEmailAndPassword(auth, signupData.email, signupData.password);
      // Auth state change will trigger useEffect and reload the page with authenticated user
      // No need to manually navigate - the useEffect will handle loading the profile
    } catch (error) {
      console.error('Error creating account:', error);

      if (error.code === 'auth/email-already-in-use') {
        setSignupError('Account already exists. Please sign in instead.');
      } else if (error.code === 'auth/invalid-email') {
        setSignupError('Invalid email address');
      } else if (error.code === 'auth/weak-password') {
        setSignupError('Password is too weak');
      } else {
        setSignupError('Failed to create account. Please try again.');
      }
      setSaving(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setSignupError('');
    setSaving(true);

    try {
      await signInWithEmailAndPassword(auth, signupData.email, signupData.password);
      // Auth state change will trigger useEffect and reload the page
    } catch (error) {
      console.error('Error signing in:', error);
      setSignupError('Invalid email or password');
      setSaving(false);
    }
  };

  const handleProfilePhotoUpload = (file) => {
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please upload an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhoto(file);
        setProfilePhotoPreview(reader.result);
        // Clear profile photo error when uploaded
        if (errors.profilePhoto) {
          setErrors({ ...errors, profilePhoto: undefined });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!phoneNumber || !phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else {
      // Validate E.164 format using libphonenumber-js
      const parsedPhone = parsePhoneNumberFromString(phoneNumber);
      if (!parsedPhone || !parsedPhone.isValid()) {
        newErrors.phoneNumber = 'Please enter a valid phone number';
      }
    }

    if (!nationality) {
      newErrors.nationality = 'Nationality is required';
    }

    // Profile photo is now optional (uploaded to Storage if provided)
    // No validation needed - handled during upload

    if (!creatorStory.trim()) {
      newErrors.creatorStory = 'Creator story is required';
    } else if (creatorStory.trim().length < 100) {
      newErrors.creatorStory = 'Creator story must be at least 100 characters';
    }

    if (!paymentMethod) {
      newErrors.paymentMethod = 'Payment method is required';
    }

    if (!paymentDetails.trim()) {
      newErrors.paymentDetails = 'Payment details are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Upload profile photo to Firebase Storage
   * @returns {Promise<string|null>} Download URL or null if no photo
   */
  const uploadProfilePhoto = async () => {
    // If no photo uploaded, return null
    if (!profilePhoto) {
      return profilePhotoPreview || null; // Keep existing URL if editing profile
    }

    try {
      // Create a unique filename with timestamp
      const timestamp = Date.now();
      const fileExtension = profilePhoto.name.split('.').pop();
      const fileName = `profile_${timestamp}.${fileExtension}`;

      // Storage path: creatorPhotos/{userId}/profile_{timestamp}.jpg
      const storagePath = `creatorPhotos/${auth.currentUser.uid}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      // Upload with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, profilePhoto);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            // Track upload progress
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
            console.log(`Upload is ${progress.toFixed(0)}% done`);
          },
          (error) => {
            // Handle upload errors
            console.error('Upload error:', error);
            setUploadProgress(0);
            reject(new Error('Failed to upload photo. Please try again.'));
          },
          async () => {
            // Upload completed successfully
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log('✅ Photo uploaded successfully:', downloadURL);
              setUploadProgress(0);
              resolve(downloadURL);
            } catch (error) {
              console.error('Error getting download URL:', error);
              reject(new Error('Failed to get photo URL. Please try again.'));
            }
          }
        );
      });
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      setUploadProgress(0);
      throw new Error('Failed to upload photo. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      alert('Please fix the errors before submitting');
      return;
    }

    if (!auth.currentUser) {
      alert('You must be signed in to continue');
      return;
    }

    setSaving(true);

    try {
      // Upload profile photo to Firebase Storage if provided
      console.log('📸 Uploading profile photo...');
      const profilePhotoUrl = await uploadProfilePhoto();
      console.log('✅ Profile photo URL:', profilePhotoUrl);

      // Save creator data to users collection with isCreator flag
      const creatorProfile = {
        socialMedia: {
          instagram: instagram?.trim() || '',
          tiktok: tiktok?.trim() || '',
          other: otherSocial?.trim() || ''
        },
        creatorStory: creatorStory?.trim() || '',
        travelExperience: travelExperience?.trim() || '',
        paymentMethod: paymentMethod || '',
        paymentDetails: paymentDetails?.trim() || '',
        profilePhotoUrl,
      };

      // Merge with existing user data (don't overwrite journeys, etc.)
      await setDoc(doc(db, 'users', auth.currentUser.uid), {
        firstName: firstName?.trim() || '',
        lastName: lastName?.trim() || '',
        email: email?.trim() || '',
        phoneNumber: phoneNumber?.trim() || '',
        nationality: nationality || '',
        language: language || '',
        gender: gender || '',
        dob: dob || '',
        preferredCurrency: preferredCurrency || 'USD',
        venmoUsername: venmoUsername?.trim() || '',
        preferAlternativeReimbursement: preferAlternativeReimbursement || false,
        isCreator: true,  // ✨ THIS IS THE KEY FLAG
        creatorProfile: creatorProfile,
        updatedAt: serverTimestamp()  // Fixed: Use serverTimestamp() instead of new Date()
      }, { merge: true }); // IMPORTANT: merge to preserve existing user data

      // Navigate to creator dashboard
      navigate('/creator-dashboard');
    } catch (error) {
      console.error('Error saving creator profile:', error);
      alert('Failed to save creator profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="creator-info-container">
        <div className="creator-info-content">
          <p style={{ textAlign: 'center', color: '#FFD700', fontSize: '1.2rem' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Show access denied if no valid payment found
  if (accessDenied) {
    return (
      <div className="creator-info-container">
        <div className="creator-info-content">
          <a href="/" className="creator-logo">
            <img src="/images/logodownsized.webp" alt="BackPass Logo" className="logo" />
          </a>

          <h1 className="creator-info-title" style={{ color: '#ef4444' }}>Access Denied</h1>
          <p className="creator-info-subtitle" style={{ maxWidth: '600px', margin: '20px auto' }}>
            This page is only accessible to creators who have completed payment.
            Please check your email for your unique creator registration link.
          </p>

          <div style={{
            marginTop: '40px',
            padding: '20px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            maxWidth: '500px',
            margin: '40px auto'
          }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '15px' }}>
              Need help?
            </p>
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.95rem' }}>
              If you've already paid and need your registration link, please contact us at{' '}
              <a href="mailto:matthew@backpass.co" style={{ color: '#FFD700' }}>
                matthew@backpass.co
              </a>
            </p>
          </div>

          <button
            onClick={() => navigate('/')}
            style={{
              marginTop: '30px',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
              color: '#072231',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  // Show signup form if user is not authenticated
  if (needsSignup) {
    return (
      <div className="creator-info-container">
        <div className="creator-info-content">
          {/* BackPass Logo */}
          <a href="/" className="creator-logo">
            <img src="/images/logodownsized.webp" alt="BackPass Logo" className="logo" />
          </a>

          <h1 className="creator-info-title">Create Your Account</h1>
          <p className="creator-info-subtitle">
            Complete your creator registration by creating your account
          </p>

          <form onSubmit={handleSignup} className="creator-info-form">
            <div className="form-section">
              <h2 className="section-title">Account Details</h2>
              <p className="section-description">
                Use the same email you used for payment
              </p>

              {/* Email */}
              <div className="form-field">
                <label htmlFor="signup-email" className="form-label">
                  Email Address * (verified)
                </label>
                <input
                  id="signup-email"
                  type="email"
                  className="form-input"
                  value={signupData.email}
                  onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                  placeholder="your.email@example.com"
                  readOnly
                  style={{ backgroundColor: 'rgba(255, 215, 0, 0.05)', cursor: 'not-allowed' }}
                  required
                />
                <p style={{ color: 'rgba(255, 215, 0, 0.7)', fontSize: '0.85rem', marginTop: '5px' }}>
                  ✓ This email has been verified from your payment
                </p>
              </div>

              {/* Password */}
              <div className="form-field">
                <label htmlFor="signup-password" className="form-label">
                  Password *
                </label>
                <input
                  id="signup-password"
                  type="password"
                  className="form-input"
                  value={signupData.password}
                  onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                  placeholder="At least 6 characters"
                  required
                />
              </div>

              {/* Confirm Password */}
              <div className="form-field">
                <label htmlFor="signup-confirm" className="form-label">
                  Confirm Password *
                </label>
                <input
                  id="signup-confirm"
                  type="password"
                  className="form-input"
                  value={signupData.confirmPassword}
                  onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                  placeholder="Re-enter your password"
                  required
                />
              </div>

              {signupError && (
                <p className="error-message" style={{ marginTop: '15px' }}>
                  {signupError}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={saving}
            >
              {saving ? 'Creating Account...' : 'Create Account & Continue'}
            </button>

            <div style={{
              marginTop: '20px',
              textAlign: 'center',
              paddingTop: '20px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: '10px' }}>
                Already have an account?
              </p>
              <button
                type="button"
                onClick={() => {
                  handleSignIn({ preventDefault: () => {} });
                }}
                style={{
                  background: 'transparent',
                  color: '#FFD700',
                  border: '1px solid rgba(255, 215, 0, 0.3)',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
                disabled={saving || !signupData.email || !signupData.password}
              >
                Sign In Instead
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="creator-info-container">
      <div className="creator-info-content">
        {/* BackPass Logo */}
        <div className="creator-logo">
          <img src="/images/logodownsized.webp" alt="BackPass Logo" className="logo" />
        </div>

        <h1 className="creator-info-title">Creator Profile</h1>

        <form onSubmit={handleSubmit} className="creator-info-form">
          {/* Basic Information */}
          <div className="form-section" style={{ paddingTop: 0 }}>
            {/* Profile Photo */}
            <div className="form-field">
              <label className="form-label">Profile Photo (Optional)</label>
              <div className="profile-photo-section">
                {/* Circular Photo Preview */}
                <div className="profile-photo-circle">
                  {profilePhotoPreview ? (
                    <img src={profilePhotoPreview} alt="Profile preview" />
                  ) : (
                    <div className="profile-photo-placeholder"></div>
                  )}
                </div>

                {/* Upload/Remove Buttons */}
                <div className="profile-photo-buttons">
                  <input
                    id="profile-photo-input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleProfilePhotoUpload(e.target.files[0])}
                    style={{ display: 'none' }}
                  />
                  {!profilePhotoPreview && (
                    <button
                      type="button"
                      className="profile-photo-btn upload-btn"
                      onClick={() => document.getElementById('profile-photo-input').click()}
                    >
                      Upload Photo
                    </button>
                  )}
                  {profilePhotoPreview && (
                    <button
                      type="button"
                      className="profile-photo-btn remove-btn"
                      onClick={() => {
                        setProfilePhoto(null);
                        setProfilePhotoPreview(null);
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              {errors.profilePhoto && <p className="error-message">{errors.profilePhoto}</p>}
            </div>

            {/* First Name */}
            <div className="form-field">
              <label htmlFor="firstName" className="form-label">
                First Name *
              </label>
              <input
                id="firstName"
                type="text"
                className={`form-input ${errors.firstName ? 'error' : ''}`}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Your first name"
              />
              {errors.firstName && <p className="error-message">{errors.firstName}</p>}
            </div>

            {/* Last Name */}
            <div className="form-field">
              <label htmlFor="lastName" className="form-label">
                Last Name *
              </label>
              <input
                id="lastName"
                type="text"
                className={`form-input ${errors.lastName ? 'error' : ''}`}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Your last name"
              />
              {errors.lastName && <p className="error-message">{errors.lastName}</p>}
            </div>

            {/* Email */}
            <div className="form-field">
              <label htmlFor="email" className="form-label">
                Email Address *
              </label>
              <input
                id="email"
                type="email"
                className={`form-input ${errors.email ? 'error' : ''}`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
              />
              {errors.email && <p className="error-message">{errors.email}</p>}
            </div>

            {/* Phone Number */}
            <div className="form-field">
              <label htmlFor="phoneNumber" className="form-label">
                Phone Number *
              </label>
              <PhoneInput
                international
                defaultCountry="US"
                placeholder="Enter phone number"
                value={phoneNumber}
                onChange={setPhoneNumber}
                className={errors.phoneNumber ? 'error' : ''}
              />
              {errors.phoneNumber && <p className="error-message">{errors.phoneNumber}</p>}
            </div>

            {/* Nationality */}
            <div className="form-field">
              <label htmlFor="nationality" className="form-label">
                Nationality *
              </label>
              <select
                id="nationality"
                className={`form-input form-select ${errors.nationality ? 'error' : ''}`}
                value={nationality}
                onChange={(e) => setNationality(e.target.value)}
              >
                <option value="">Select your nationality...</option>
                {COUNTRY_LIST.map(country => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
              {errors.nationality && <p className="error-message">{errors.nationality}</p>}
            </div>

            {/* Language */}
            <div className="form-field">
              <label htmlFor="language" className="form-label">
                Language
              </label>
              <select
                id="language"
                className="form-input form-select"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="">Select your language...</option>
                {LANGUAGE_LIST.map(lang => (
                  <option key={lang} value={lang}>
                    {lang}
                  </option>
                ))}
              </select>
            </div>

            {/* Gender */}
            <div className="form-field">
              <label htmlFor="gender" className="form-label">
                Gender
              </label>
              <select
                id="gender"
                className="form-input form-select"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="">Select your gender...</option>
                <option value="Man">Man</option>
                <option value="Woman">Woman</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Date of Birth */}
            <div className="form-field">
              <label htmlFor="dob" className="form-label">
                Date of Birth
              </label>
              <input
                id="dob"
                type="date"
                className="form-input"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
              />
            </div>

            {/* Preferred Currency */}
            <div className="form-field">
              <label htmlFor="preferredCurrency" className="form-label">
                Preferred Currency
              </label>
              <select
                id="preferredCurrency"
                className="form-input form-select"
                value={preferredCurrency}
                onChange={(e) => setPreferredCurrency(e.target.value)}
              >
                {CURRENCY_LIST.map(currency => (
                  <option key={currency.code} value={currency.code}>
                    {currency.symbol} {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Venmo Username */}
            <div className="form-field">
              <label htmlFor="venmoUsername" className="form-label">
                Venmo Username
              </label>
              <input
                id="venmoUsername"
                type="text"
                className="form-input"
                value={venmoUsername}
                onChange={(e) => setVenmoUsername(e.target.value)}
                placeholder="@username (optional)"
              />
              <p className="payment-note">
                For reimbursements and trip payments
              </p>
            </div>

            {/* Alternative Reimbursement Preference */}
            <div className="form-field">
              <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer', justifyContent:'center' }}>
                <input
                  type="checkbox"
                  checked={preferAlternativeReimbursement}
                  onChange={(e) => setPreferAlternativeReimbursement(e.target.checked)}
                  style={{ width: 'auto' }}
                />
                <span style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.95rem' }}>
                  I prefer alternative reimbursement methods to Venmo
                </span>
              </label>
            </div>
          </div>

          {/* Social Media */}
          <div className="form-section">
            <h2 className="section-title">Social Media</h2>
            <p className="section-description">
              Help us promote your routes and connect with your audience
            </p>

            {/* Instagram */}
            <div className="form-field">
              <label htmlFor="instagram" className="form-label">
                Instagram Handle
              </label>
              <div className="social-input-wrapper">
                <span className="social-prefix">@</span>
                <input
                  id="instagram"
                  type="text"
                  className="form-input social-input"
                  value={instagram}
                  onChange={(e) => {
                    // Remove leading @ if user types it
                    const value = e.target.value.replace(/^@+/, '');
                    setInstagram(value);
                  }}
                  placeholder="yourusername"
                />
              </div>
            </div>

            {/* TikTok */}
            <div className="form-field">
              <label htmlFor="tiktok" className="form-label">
                TikTok Handle
              </label>
              <div className="social-input-wrapper">
                <span className="social-prefix">@</span>
                <input
                  id="tiktok"
                  type="text"
                  className="form-input social-input"
                  value={tiktok}
                  onChange={(e) => {
                    // Remove leading @ if user types it
                    const value = e.target.value.replace(/^@+/, '');
                    setTiktok(value);
                  }}
                  placeholder="yourusername"
                />
              </div>
            </div>

            {/* Other Social */}
            <div className="form-field">
              <label htmlFor="otherSocial" className="form-label">
                Other Social Media
              </label>
              <input
                id="otherSocial"
                type="text"
                className="form-input"
                value={otherSocial}
                onChange={(e) => setOtherSocial(e.target.value)}
                placeholder="Twitter, blog, website, etc."
              />
            </div>
          </div>

          {/* Creator Story */}
          <div className="form-section">
            <h2 className="section-title">Your Story</h2>

            {/* Creator Story */}
            <div className="form-field">
              <label htmlFor="creatorStory" className="form-label">
                Tell Us About Yourself *
              </label>
              <textarea
                id="creatorStory"
                className={`form-textarea ${errors.creatorStory ? 'error' : ''}`}
                value={creatorStory}
                onChange={(e) => setCreatorStory(e.target.value)}
                placeholder="Share your background, travel philosophy, and what makes you unique... (minimum 100 characters)"
                rows={6}
              />
              <p className="character-count">
                {creatorStory.length} / 100 minimum characters
              </p>
              {errors.creatorStory && <p className="error-message">{errors.creatorStory}</p>}
            </div>

            {/* Travel Experience */}
            <div className="form-field">
              <label htmlFor="travelExperience" className="form-label">
                Travel Experience (Optional)
              </label>
              <textarea
                id="travelExperience"
                className="form-textarea"
                value={travelExperience}
                onChange={(e) => setTravelExperience(e.target.value)}
                placeholder="Countries you've visited, years of travel experience, travel style, etc."
                rows={4}
              />
            </div>
          </div>

          {/* Payment Information */}
          <div className="form-section">
            <h2 className="section-title">Payment Information</h2>
            <p className="section-description">
              This is how we pay you the $25. If you're doing international bank info or anything complicated we'll figure it out manually.
            </p>

            {/* Payment Method */}
            <div className="form-field">
              <label htmlFor="paymentMethod" className="form-label">
                Preferred Payment Method *
              </label>
              <select
                id="paymentMethod"
                className={`form-input form-select ${errors.paymentMethod ? 'error' : ''}`}
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <option value="">Select payment method...</option>
                <option value="PayPal">PayPal</option>
                <option value="Venmo">Venmo</option>
                <option value="Zelle">Zelle</option>
                <option value="Bank Transfer">Bank Transfer (ACH)</option>
                <option value="Wire Transfer">International Wire Transfer</option>
                <option value="Other">Other</option>
              </select>
              {errors.paymentMethod && <p className="error-message">{errors.paymentMethod}</p>}
            </div>

            {/* Payment Details */}
            <div className="form-field">
              <label htmlFor="paymentDetails" className="form-label">
                Payment Account Details *
              </label>
              <textarea
                id="paymentDetails"
                className={`form-textarea ${errors.paymentDetails ? 'error' : ''}`}
                value={paymentDetails}
                onChange={(e) => setPaymentDetails(e.target.value)}
                placeholder="Provide your account info (e.g., PayPal email, Venmo username, bank account details, etc.)"
                rows={4}
              />
              {errors.paymentDetails && <p className="error-message">{errors.paymentDetails}</p>}
            </div>
          </div>

          {/* Upload Progress */}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <div style={{
              marginBottom: '20px',
              padding: '15px',
              background: 'rgba(255, 215, 0, 0.1)',
              border: '1px solid rgba(255, 215, 0, 0.3)',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <p style={{ color: '#FFD700', marginBottom: '10px', fontWeight: '600' }}>
                Uploading profile photo... {uploadProgress.toFixed(0)}%
              </p>
              <div style={{
                width: '100%',
                height: '8px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${uploadProgress}%`,
                  height: '100%',
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="submit-button"
            disabled={saving}
          >
            {saving
              ? (uploadProgress > 0 && uploadProgress < 100
                  ? `Uploading... ${uploadProgress.toFixed(0)}%`
                  : 'Saving profile...')
              : 'Continue to the Creator Dashboard'}
          </button>
        </form>
      </div>
    </div>
  );
}
